<template>
  <ResponsiveLayout
    :progress="progress"
    :show-lang-picker="false"
    sticky-actions
    theme="blue-actions"
  >
    <template #header_start>
      <ButtonBack @click="emit('back')" />
    </template>
    <template #header_middle>
      <div class="kds-typography-title-medium">
        {{ $t('b2b_self_test_generic_assessment') }}
      </div>
    </template>
    <div class="kds-main-wrapper">
      <h3
        class="kds-typography-title-xlarge"
        v-html="$t('b2b__red_flags__sub_title')"
      />
    </div>
    <div
      v-for="(item, index) in items"
      :key="index"
      class="red-flag"
    >
      <div class="red-flag-question">
        <div
          class="red-flag-question__title"
          v-html="item.question"
        />
        <DropdownArrowControl
          :css-classes="['dropdown-arrow-control-red-flag']"
          :default-state="item.checked"
          :disabled="formIsDisabled"
          @click="
            (nextState) => {
              switchMedicalConditionControl(nextState, item)
            }
          "
        />
      </div>
      <div :class="getRedFlagAnswerCSSClasses(item)">
        <div
          :aria-hidden="!item.checked"
          class="red-flag-description"
          v-html="item.answer"
        />
      </div>
    </div>

    <template #actions>
      <CheckboxField
        data-qa="precautions-red-flag-checkbox"
        :label="$t('b2b__red_flags__checkbox_label')"
        :model-value="redFlagApproved"
        @update:model-value="handleConfirmationBoxChange"
      />
      <BaseButton
        :disabled="!isFormValid || formIsDisabled"
        :text="$t('b2b_sign_up_submit_button_text')"
        data-qa="submit"
        @click="next"
      />
    </template>
  </ResponsiveLayout>
</template>

<script setup>
import DropdownArrowControl from '@shared/components/DropdownArrowControl.vue'
import { sendBrazeEvent } from '@shared/api-endpoints'

import precautions from '@shared/modules/assessment/config/precautions-msk-eu.json'
import { useI18n } from 'petite-vue-i18n'
import useSelfTestStore from '@shared/modules/assessment/stores/useSelfTestStore.js'
import { computed, ref } from 'vue'
import ResponsiveLayout from '@shared/components/ResponsiveLayout.vue'
import ButtonBack from '@shared/components/ButtonBack.vue'
import BaseButton from '@shared/components/BaseButton.vue'
import CheckboxField from '@shared/components/form/CheckboxField.vue'

const { t } = useI18n()
const selfTestStore = useSelfTestStore()

const props = defineProps({
  progress: {
    type: Number,
    default: null,
  },
  handleNextEvent: {
    type: Function,
    default: () => {},
  },
})

const emit = defineEmits(['back'])

const items = ref(
  precautions
    .map((precaution) => ({
      question: t(precaution.question),
      answer: t(precaution.answer),
    }))
    .filter((item) => item.question !== '' || item.answer !== ''),
)

const redFlagApproved = computed(() => selfTestStore.redFlagApproved)

const isFormValid = computed(() => redFlagApproved.value)

const handleConfirmationBoxChange = (checked) => {
  selfTestStore.redFlagApproved = checked
}

const formIsDisabled = ref(false)

const next = async () => {
  if (formIsDisabled.value) {
    return
  }
  formIsDisabled.value = true

  await sendBrazeEvent({
    name: 'milestone_red_flags_approved',
  })

  props.handleNextEvent()
}

const getRedFlagAnswerCSSClasses = (item) => [
  'red-flag-answer',
  !item.checked ? 'red-flag-answer--hidden' : '',
]

const switchMedicalConditionControl = (nextState, item) => {
  item.checked = nextState
}
</script>

<style lang="scss">
.dropdown-arrow-control-red-flag {
  width: 24px;
  height: 20px;
}

.red-flag {
  border-bottom: 1px solid $kds-color-background-subtle;
  overflow: hidden;
  min-height: 56px;
}

.red-flag-question {
  display: flex;
  justify-content: space-between;
  position: relative;

  padding: 18px 0;
  margin-right: 6px;

  &__title {
    flex-basis: 80%;

    @include kds-typography-title-medium;
  }
}

.red-flag-answer {
  opacity: 1;
  transition: opacity 0.75s linear;

  &--hidden {
    opacity: 0;
    height: 0;
  }
}

.red-flag-description {
  @include kds-typography-paragraph-medium;
}
</style>
